import { motion } from 'motion/react';
import { XIcon, Stethoscope, BookOpen } from 'lucide-react';
import type { ReportFormData } from '../types';

export interface BatchItem {
  id: string;
  formData: ReportFormData;
  label: string;
}

interface BatchItemCardProps {
  item: BatchItem;
  index: number;
  isEditing: boolean;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  language: string;
}

export function BatchItemCard({ item, index, isEditing, onEdit, onRemove, language }: BatchItemCardProps) {
  const isRTL = language === 'he';
  const isTreatment = item.formData.reportTypeCode === 'TREATMENT';
  const qty = item.formData.quantity;
  const time = item.formData.startTime && item.formData.endTime
    ? `${item.formData.startTime}–${item.formData.endTime}`
    : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: isRTL ? 30 : -30 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
    >
      <div
        onClick={() => onEdit(index)}
        className="flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer active:scale-[0.98] transition-all batch-btn-glow"
        style={{
          backgroundColor: isEditing
            ? 'color-mix(in srgb, var(--color-primary-500) 15%, white)'
            : 'white',
          border: isEditing
            ? '1.5px solid var(--color-primary-500)'
            : '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        {/* Number */}
        <span className="text-xs font-bold flex-shrink-0" style={{ color: '#94a3b8' }}>
          #{index + 1}
        </span>

        {/* Icon */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: isTreatment
              ? 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)'
              : 'rgba(76, 175, 80, 0.1)',
          }}
        >
          {isTreatment
            ? <Stethoscope className="h-4 w-4" style={{ color: 'var(--color-primary-600)' }} />
            : <BookOpen className="h-4 w-4" style={{ color: '#43a047' }} />
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>
              {item.label}
            </span>
            {qty > 1 && (
              <span
                className="text-[10px] font-bold px-1.5 py-px rounded-md leading-tight"
                style={{
                  backgroundColor: 'var(--color-primary-500)',
                  color: '#fff',
                }}
              >
                ×{qty}
              </span>
            )}
          </div>
          {time && (
            <span className="text-xs text-slate-400" dir="ltr">{time}</span>
          )}
        </div>

        {/* Remove */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer active:scale-90 transition-all hover:bg-red-50"
          style={{ color: '#cbd5e1' }}
        >
          <XIcon className="h-3.5 w-3.5 hover:text-red-400 transition-colors" />
        </button>
      </div>
    </motion.div>
  );
}
